import type { WorkOrderPriceItem } from 'gql/graphql';
import { CostGroupEnum, WorkOrderPriceStatusEnum, WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { useWorkOrderVorTypeAndStatusFollowers } from 'pages/Project/UerProject/WorkOrder/hooks/useWorkOrderVorTypeAndStatusFollowers';
import { checkCanEditDistribution } from 'pages/Project/UerProject/WorkOrder/WorkOrderOthersTable/checkActKC2OtherHaveNoZeroPrices';

import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { WorkOrderMenuActionTypes } from '../../types';

const menuGroups = [
    {
        id: 1,
    },
    {
        id: 2,
    },
];
const hasActPlanVolumeMessage = 'Имеется фактическое выполнение. Для редактирования очистите значения в ячейках Акта ';

export const useMenuItemsByRowType = ({
    rowData,
    openModalHandler,
    workOrderStatus,
    isDeleteDisabled,
}: {
    rowData: WorkOrderPriceItem | null | undefined;
    openModalHandler: (type: WorkOrderMenuActionTypes, rowData: WorkOrderPriceItem, options?: any) => void;
    workOrderStatus?: WorkOrderStatusEnum;
    isDeleteDisabled: boolean;
}) => {
    const { id: projectId, orderId } = useParams();
    const workOrderId = Number(orderId);
    const { projectData } = useProjectData(projectId, false);

    const { workOrderVorTypeData } = useWorkOrderVorTypeAndStatusFollowers(workOrderId, false);

    const isActsKc2AnalyticsEnabled = useMemo(
        () =>
            (workOrderStatus === WorkOrderStatusEnum.InProgress ||
                workOrderStatus === WorkOrderStatusEnum.Completed ||
                (workOrderStatus === WorkOrderStatusEnum.Delayed &&
                    workOrderVorTypeData?.uerWorkOrder?.data?.statusFollowers?.includes(
                        WorkOrderStatusEnum.InProgress,
                    ))) &&
            rowData?.hasCompletedSkzsActs,
        [rowData?.hasCompletedSkzsActs, workOrderStatus, workOrderVorTypeData?.uerWorkOrder?.data?.statusFollowers],
    );

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();
    const analyticsData = useMemo(
        () => ({
            projectId,
            contractId: projectData?.contractId,
            orderId: workOrderId,
            orderStatus: workOrderStatus,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        }),
        [
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            workOrderId,
            workOrderStatus,
        ],
    );
    const sendAnalyticsData = useCallback(
        (componentId: string) => {
            sendGpnAnalyticsEvent({
                componentId,
                ...analyticsData,
            });
        },
        [analyticsData, sendGpnAnalyticsEvent],
    );
    const runActionByType = useCallback(
        (type: WorkOrderMenuActionTypes, options?: any) => () => {
            if (rowData) {
                openModalHandler(type, rowData, options);
            }
        },
        [openModalHandler, rowData],
    );
    const editNoteItemMenu = useMemo(
        () =>
            workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          label: 'Редактировать примечание',
                          action: runActionByType(WorkOrderMenuActionTypes.editNote),
                      },
                  ]
                : [],
        [workOrderStatus, runActionByType],
    );
    const removeItemMenu = useMemo(
        () => [
            {
                label: 'Удалить',
                action: runActionByType(WorkOrderMenuActionTypes.singleDelete),
                disabled: isDeleteDisabled,
            },
        ],
        [isDeleteDisabled, runActionByType],
    );

    const rootObjectItems = useMemo(
        () => [
            {
                label: 'Добавить Акт КС-2 на СМР',
                action: () => {
                    runActionByType(WorkOrderMenuActionTypes.addKC2, {
                        costGroup: CostGroupEnum.Cmr,
                    })();
                    sendAnalyticsData("'rim_add act KC button_pressed'");
                },
            },
            {
                label: 'Добавить Акт КС-2 на прочие затраты',
                action: () => {
                    runActionByType(WorkOrderMenuActionTypes.addKC2, {
                        costGroup: CostGroupEnum.Others,
                    })();
                    sendAnalyticsData('rim_add act KC button_pressed');
                },
            },
            {
                label: 'Просмотреть диаграмму фактического освоения',
                action: () => {
                    runActionByType(WorkOrderMenuActionTypes.actsKc2Analytics)();
                },
                disabled: !isActsKc2AnalyticsEnabled,
                haveTooltip: !rowData?.hasCompletedSkzsActs,
                tooltipName: 'doesNotHaveCompletedSkzsActs',
                tooltipMsg: 'Для отображения диаграммы необходимо наличие согласованного Акта из СКзС',
            },
        ],
        [isActsKc2AnalyticsEnabled, rowData?.hasCompletedSkzsActs, runActionByType, sendAnalyticsData],
    );
    const isStatusDraftOrNew = useMemo(
        () =>
            rowData?.status &&
            [WorkOrderPriceStatusEnum.Draft, WorkOrderPriceStatusEnum.NewRecord].includes(
                rowData.status as WorkOrderPriceStatusEnum,
            ),
        [rowData?.status],
    );
    const isEditDisabled = useMemo(() => {
        if (rowData) {
            return (
                (workOrderStatus === WorkOrderStatusEnum.InProgress && !rowData?.isActKC2Price) ||
                [WorkOrderPriceStatusEnum.ContractCondition, WorkOrderPriceStatusEnum.AdditionalCondition].includes(
                    rowData?.status as WorkOrderPriceStatusEnum,
                )
            );
        }

        return true;
    }, [rowData, workOrderStatus]);
    const isEditDisabledByEmptyPlanVolume = useMemo(() => rowData && !rowData.hasEmptyActPlanVolume, [rowData]);
    const uerMenu = useMemo(
        () => [
            ...(isStatusDraftOrNew
                ? [
                      {
                          label: 'Редактировать',
                          action: () => {
                              runActionByType(WorkOrderMenuActionTypes.editUer)();
                              sendAnalyticsData('rim_edit price button_selected');
                          },
                          disabled: isEditDisabledByEmptyPlanVolume || isEditDisabled,
                          haveTooltip: isEditDisabledByEmptyPlanVolume,
                          tooltipName: 'hasEmptyActPlanVolume',
                          tooltipMsg: hasActPlanVolumeMessage,
                      },
                  ]
                : []),
            {
                label: 'Добавить МТР',
                action: () => {
                    runActionByType(WorkOrderMenuActionTypes.addMtr)();

                    if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                        sendAnalyticsData('rim_NZ_add MTR button_pressed');
                    } else if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                        sendAnalyticsData('rim_act KC_add MTR button_pressed');
                    }
                },
            },
            {
                label: 'Добавить коэффициент',
                action: () => {
                    runActionByType(WorkOrderMenuActionTypes.addCoefficient)();
                    if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
                        sendAnalyticsData('rim_NZ_add coef button_pressed');
                    } else if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                        sendAnalyticsData('rim_act KC_add coef button_pressed');
                    }
                },
            },
            ...editNoteItemMenu,
            ...removeItemMenu,
        ],
        [
            editNoteItemMenu,
            isEditDisabled,
            isEditDisabledByEmptyPlanVolume,
            isStatusDraftOrNew,
            removeItemMenu,
            runActionByType,
            sendAnalyticsData,
            workOrderStatus,
        ],
    );
    const isDuplicate = useMemo(() => rowData?.hasDuplicates, [rowData]);

    const mtrMenu = useMemo(
        () => [
            ...(rowData?.status === WorkOrderPriceStatusEnum.Draft && isDuplicate
                ? [
                      {
                          label: 'Сопоставить с Прейскурантом МТР',
                          action: runActionByType(WorkOrderMenuActionTypes.matchMtr),
                      },
                  ]
                : []),
            ...(isStatusDraftOrNew
                ? [
                      {
                          label: 'Редактировать',
                          action: runActionByType(WorkOrderMenuActionTypes.editMtr),
                          disabled: isEditDisabledByEmptyPlanVolume || isEditDisabled,
                          haveTooltip: isEditDisabledByEmptyPlanVolume,
                          tooltipName: 'hasEmptyActPlanVolume',
                          tooltipMsg: hasActPlanVolumeMessage,
                      },
                  ]
                : editNoteItemMenu),
            ...removeItemMenu,
        ],
        [
            editNoteItemMenu,
            isDuplicate,
            isEditDisabled,
            isEditDisabledByEmptyPlanVolume,
            isStatusDraftOrNew,
            removeItemMenu,
            rowData?.status,
            runActionByType,
        ],
    );

    const coefficientMenu = useMemo(
        () => [
            ...editNoteItemMenu,
            {
                label: 'Редактировать',
                action: () => {
                    runActionByType(WorkOrderMenuActionTypes.editCoefficient)();
                },
                disabled:
                    (rowData?.status &&
                        ![WorkOrderPriceStatusEnum.Draft, WorkOrderPriceStatusEnum.NewRecord].includes(
                            rowData.status,
                        )) ||
                    isEditDisabledByEmptyPlanVolume ||
                    isEditDisabled,
                haveTooltip: isEditDisabledByEmptyPlanVolume,
                tooltipName: 'hasEmptyActPlanVolume',
                tooltipMsg: hasActPlanVolumeMessage,
            },
            ...removeItemMenu,
        ],
        [
            editNoteItemMenu,
            isEditDisabled,
            isEditDisabledByEmptyPlanVolume,
            removeItemMenu,
            rowData?.status,
            runActionByType,
        ],
    );

    const othersMenu = useMemo(
        () => [
            ...(checkCanEditDistribution(rowData, workOrderStatus)
                ? [
                      {
                          label: 'Изменить распределение',
                          action: runActionByType(WorkOrderMenuActionTypes.editOtherDistribution),
                      },
                  ]
                : []),

            {
                label: 'Просмотреть',
                action: runActionByType(WorkOrderMenuActionTypes.viewOther),
            },
            ...(!rowData?.isActKC2Price ? [] : [...editNoteItemMenu]),
            ...removeItemMenu,
        ],
        [editNoteItemMenu, removeItemMenu, rowData, runActionByType, workOrderStatus],
    );

    const getMenuItems = useMemo<Partial<Record<WorkOrderPriceTypeEnum | '', any[]>>>(
        () => ({
            [WorkOrderPriceTypeEnum.RootObject]: rootObjectItems,
            [WorkOrderPriceTypeEnum.Uer]: uerMenu,
            [WorkOrderPriceTypeEnum.Mtr]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrContractorMaterial]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrContractorEquipment]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrCustomerMaterial]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrCustomerEquipment]: mtrMenu,
            [WorkOrderPriceTypeEnum.Coefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.ConstraintCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.TechPartCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.DismantlingCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.Other]: othersMenu,
        }),
        [coefficientMenu, mtrMenu, othersMenu, rootObjectItems, uerMenu],
    );

    return {
        menuItems: getMenuItems[rowData?.type ?? ''] ?? [],
        menuGroups,
    };
};
